import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Factory from '@/Factories/Factory';
import ControlFactory from '@/Factories/ControlFactory';
import * as interfaces from '#/CoreFrameInterfaces';
import EditContext from '@/ViewModels/Contexts/EditContext';
import InlineEditContext from '@/ViewModels/Contexts/InlineEditContext';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import RADApplication from '@/Management/RADApplication';
import ResourceManager from '@/Management/ResourceManager';
import LocatorHelper from '@/Utility/LocatorHelper';
import Utilities from '@/Utility/Utilities';
import {
    Dictionary,
    GridColumn,
    IInlineEditingGridViewModelConstructorParameter,
    IReadOnlyPrinterStatic,
    IVerticalResizeOptions
} from '#/DefaultViewExtensionInterfaces';
import { ISortByCol, singleOrMultiple } from 'rad-coreframe-grid';
import { IRefreshable, IInlineEditingUIListener } from '#/DefaultViewExtensionInterfaces';
import InlineEditingGridManager from '@/Components/InlineEditingGrid/InlineEditingGridManager';
import InlineEditingGridColumnHelper from '@/Components/InlineEditingGrid/InlineEditingGridColumnHelper';
import FilterHelper from '@/Utility/FilterHelper';
import StorageHelper from '@/Utility/StorageHelper';
import User from '@/Model/User';

export default class InlineEditingGridViewModel extends ControlViewModel implements IRefreshable {

    public columnDefinition: KnockoutObservable<CoreFrameDTOs.IInlineEditingGridColumnDTO[]>;
    public columns: KnockoutObservable<GridColumn[]>;
    /**
    * Definiert die initiale Breite der Spalten
    */
    public columnPreset: KnockoutObservable<object>;
    public dataSource: KnockoutObservableArray<CoreFrameDTOs.IGenericDTO>;
    public isMultiColumnSort: KnockoutObservable<boolean>;
    public height: KnockoutObservable<string>;
    public defaultHeight: KnockoutObservable<string>;
    private hasEditRight: KnockoutObservable<boolean>;
    public isEditable: KnockoutObservable<boolean>;
    public rowIdName: KnockoutObservable<string>;
    protected hasAddRight: KnockoutObservable<boolean>;
    public canAdd: KnockoutObservable<boolean>;
    public fireEditWithoutClick: KnockoutObservable<string>;
    public editMode: singleOrMultiple;

    public detailViewItem: CoreFrameDTOs.IDVGItemDTO;
    public detailViewContext: DetailViewContext;
    protected inlineEditingContext: KnockoutObservable<CoreFrameDTOs.IInlineEditingContextDTO>;
    protected iterator: CoreFrameDTOs.IIteratorDTO;

    protected lockGroupID: string;
    protected excludedMembers: string[];
    protected visibleMembers: string[];

    protected orderBy: KnockoutObservable<string>;
    private editContexts: { [key: string]: InlineEditContext } = {};
    private lockedObjects: number[] = [];
    public totalRows: KnockoutObservable<number>;

    private _propertyCache: string[];
    private _isRefreshing: boolean = false;
    private onPropertyValueUpdatedCallbacks: ((context: InlineEditContext, property: string) => void)[];
    private onAssociationUpdatedCallbacks: ((context: InlineEditContext, relation: string) => void)[];
    public fullExtent: KnockoutObservable<string>;

    /**
     * Enthält alle laufenden Promises für die Speicherung
     */
    private _savePromises: JQueryPromise<boolean>[];

    public verticalResize: IVerticalResizeOptions;
    public showNoEntriesLabel: boolean;

    public constructor(params: IInlineEditingGridViewModelConstructorParameter) {
        super(params);

        this.undisposeProperties.push('detailViewContext');

        this.detailViewItem = params.detailViewItem;
        this.detailViewContext = params.detailViewContext;
        this.inlineEditingContext = this.observableOrDefault(null);
        this.excludedMembers = this.paramOrDefault(params.excludedMembers, []);
        this.visibleMembers = this.paramOrDefault(params.visibleMembers, []);

        this.onPropertyValueUpdatedCallbacks = [];
        this.onAssociationUpdatedCallbacks = [];

        this.registerUIListener();

        this.rowIdName = this.observableOrDefault('pdoId');
        this.columnDefinition = this.observableOrDefault([]);
        this.columns = this.observableOrDefault(params.columns, []);
        this.columnPreset = StorageHelper.bind(this.detailViewItem.listView.name + '_IEG_ColumnPreset', {});
        this.dataSource = this.observableOrDefault(params.dataSource, []).extend({ notify: 'always' }) as KnockoutObservableArray<any>;
        this.hasEditRight = this.observableOrDefault(false);
        this.isEditable = this.defineComputed(() => {
            return this.hasEditRight() && !this.detailViewContext.isSummaryViewMode() && this.isEnabled();
        });
        this.hasAddRight = this.observableOrDefault(false);
        this.canAdd = this.defineComputed(() => {
            return this.hasAddRight() && this.isEnabled();
        });
        this.height = this.observableOrDefault(params.height, '214px');
        this.defaultHeight = this.observableOrDefault('214px');
        this.fireEditWithoutClick = this.observableOrDefault('');
        this.editMode = this.paramOrDefault(params.editMode, 'single');
        this.isEnabled = this.observableOrDefault(params.isEnabled, true);
        this.isMultiColumnSort = this.observableOrDefault(params.isMultiColumnSort, false);

        if (params.orderBy) {
            this.orderBy = this.observableOrDefault(params.orderBy, '');
        }
        else {
            this.orderBy = this.observableOrDefault('');
            this.setOrderBy([]);
        }

        this.totalRows = this.observableOrDefault(0);
        this.registerColumnDefinitionSubscriptions();
        this.registerForMessages();
        this._savePromises = [];

        if (params.autoInit !== false)
            this.init();

        this.fullExtent = this.observableOrDefault(this.detailViewContext.defaultIterators[this.detailViewItem.name]?.fullExtentCount, this.dataSource.length.toString())

        this.verticalResize = this.createVerticalResizeOptions();
        this.showNoEntriesLabel = this.paramOrDefault(params.showNoEntriesLabel, false);
    }

    private createVerticalResizeOptions(): IVerticalResizeOptions {
        const dvName = this.detailViewContext?.detailView()?.name;
        const dviName = this.detailViewItem?.name;
        return {
            isUserResizeEnabled: true,
            storageKeyPrefix: dvName && dviName ? `${dvName}#${dviName}` : null
        };
    }

    public onPropertyValueChanged(callback: (context: InlineEditContext, property: string) => void): void {
        this.onPropertyValueUpdatedCallbacks.push(callback);
    }

    public onAssocationChanged(callback: (context: InlineEditContext, relation: string) => void): void {
        this.onAssociationUpdatedCallbacks.push(callback);
    }

    protected registerUIListener(): void {
        const rootContext = this.detailViewContext.getRootDetailViewContext();
        rootContext.uiListener.registerOnBeforeApplyAction(this.onBeforeApplyDetailViewContext.bind(this));
    }

    protected raiseRootOnAssociationUpdated(): void {
        const rootContext = this.detailViewContext.getRootDetailViewContext();
        const uiListener = rootContext?.uiListener;
        if (!uiListener)
            return;

        const context = this.inlineEditingContext();
        if (context)
            uiListener.onAssociationUpdated(rootContext, context.contextRelationName);
    }

    protected onBeforeApplyDetailViewContext(): JQueryPromise<boolean> {
        return $.when.apply($, this._savePromises).then(() => {
            if (this.lockedObjects.length > 0) {
                let resource = ResourceManager.getInstance().get('INLINE_EDITING_OPEN_CHANGES');
                let vm = MessageBoxViewModel.createWarningMessageBox(resource);
                RADApplication.getInstance().showMessageBox(vm);
                return Utilities.resolveImmediately(false);
            }
            else
                return Utilities.resolveImmediately(true);
        });
    }

    private onRowAssociationUpdated(context: InlineEditContext, relation: string): void {
        this.onAssociationUpdatedCallbacks.forEach(x => x(context, relation));
    }

    private onRowPropertyValueUpdated(context: InlineEditContext, property: string): void {
        this.onPropertyValueUpdatedCallbacks.forEach(x => x(context, property));
    }

    protected init(): JQuery.Promise<boolean> {
        if (this.isDisposedOrDisposing() || this.detailViewContext.isDisposedOrDisposing() || this.detailViewContext.isDeletedOrDeleting())
            return Utilities.resolveImmediately(false);

        this.increaseBusyCounter();
        return RADEnvironmentProxy.getInstance().createInlineEditingContext(this.getCreateContextParameter())
            .then((result) => {
                if (this.isDisposedOrDisposing() || this.detailViewContext.isDisposedOrDisposing() || this.detailViewContext.isDeletedOrDeleting())
                    return undefined;

                return this.handleContextDto(result);
            }).always(() => {
                this.decreaseBusyCounter();
            }).promise();
    }

    private handleContextDto(result: CoreFrameDTOs.IInlineEditingContextDTO, onlyData: boolean = false): JQuery.Promise<boolean> {
        if (!this.validateStatus(result))
            return undefined;

        this.inlineEditingContext(result);
        this.lockGroupID = result.lockGroupID;
        this.hasAddRight(result.hasCreateRight);
        this.hasEditRight(result.hasWriteRight);

        if (this.isDisposed)
            return undefined;

        return $.when(this.createColumnDefinitionInternal(result)).then((result) => {
            if (this.isDisposed)
                return undefined;

            this.columnDefinition(result);

            return this.refresh();
        });
    }

    private createColumnDefinitionInternal(inlineEditingContext: CoreFrameDTOs.IInlineEditingContextDTO): JQueryPromise<CoreFrameDTOs.IInlineEditingGridColumnDTO[]> {
        return $.when(this.createColumnDefinition(inlineEditingContext)).then((columnDefinition) => {
            if (!this.isDisposed) {
                if (this.isUiListenerInlineUiListener(this.detailViewContext.uiListener)) {
                    return this.detailViewContext.uiListener.onColumnDefinitionCreated(this.detailViewContext, inlineEditingContext, columnDefinition);
                }
            }
            
            return columnDefinition;
        });
    }

    protected createColumnDefinition(inlineEditingContext: CoreFrameDTOs.IInlineEditingContextDTO): JQueryPromise<CoreFrameDTOs.IInlineEditingGridColumnDTO[]> | CoreFrameDTOs.IInlineEditingGridColumnDTO[] {
        return InlineEditingGridColumnHelper.createColumnDefinitionFromDetailView(this.detailViewItem.listView, inlineEditingContext.detailView, {
            excludedMembers: this.excludedMembers,
            visibleMembers: this.visibleMembers
        });
    }

    /**
     * Gibt an, ob der aktuelle UIListener für das InlineEditingGrid relevant ist
     * @param uiListener
     */
    protected isUiListenerInlineUiListener(uiListener: any): uiListener is IInlineEditingUIListener {
        if (uiListener == null)
            return false;
        
        return (uiListener as IInlineEditingUIListener).onColumnDefinitionCreated != null;
    }

    protected transformRows(result: CoreFrameDTOs.IInlineEditingContextDataDTO): CoreFrameDTOs.IGenericDTO[] {
        return result.subjects;
    }

    protected shouldRemoveDefaultProperties(): boolean {
        return true;
    }

    protected getProperties(): string[] {
        return this._propertyCache;
    }

    protected getPropertiesForIterator(): string[] {
        return this._propertyCache.filter(x => this.inlineEditingContext().toManyAssociations.indexOf(x) == -1);
    }

    public refresh(): JQuery.Promise<boolean> {
        if (this.isDisposed)
            return undefined;
        if (this._isRefreshing)
            return Utilities.resolveImmediately(true);
        this._isRefreshing = true;
        this.increaseBusyCounter();
        this._propertyCache = InlineEditingGridColumnHelper.getNeededPropertiesForEditing(this.columns(), this.inlineEditingContext())
        return this.deleteIterator(this.iterator)
            .then(() => {
                if (this.isDisposed)
                    return undefined;

                return this.createIterator();
            }).then((iterator) => {
                if (!this.validateStatus(iterator))
                    return false;

                this.totalRows(parseInt(iterator.extentCount));
                this.iterator = iterator;
                this.fullExtent(iterator.fullExtentCount);

                if (this.totalRows() == 0) {
                    this.dataSource([]);
                    return true;
                }

                return this.getObjects().then((data) => {
                    if (!this.validateStatus(data))
                        return false;

                    const transformedRows = this.transformRows(data);
                    this.dataSource(transformedRows);

                    return true;
                })
            }).always(() => {
                this.decreaseBusyCounter();
                this._isRefreshing = false;
                this.onAfterRefresh();
            }).promise();
    }

    protected onAfterRefresh(): void {
        // Hook Method
    }

    protected createIterator(): JQuery.Promise<CoreFrameDTOs.IIteratorDTO> {
        if (this.isDisposed)
            return undefined;

        const context = this.inlineEditingContext();
        return RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
            pdo_id: context?.contextObjectPdoId,
            assoziationName: context?.contextRelationName,
            pdTypeName: context?.contextObjectPdTypeName,
            filter: '',
            orderby: this.orderBy(),
            properties: this.getPropertiesForIterator(),
            retrieveExtentCount: true,
            itOperator: null
        }).promise();
    }

    protected getObjects(): JQuery.Promise<CoreFrameDTOs.IInlineEditingContextDataDTO> {
        if (this.isDisposed)
            return undefined;

        const context = this.inlineEditingContext();
        return RADEnvironmentProxy.getInstance().getInlineEditingObjects({
            inlineEditingContextId: context?.id,
            iteratorId: this.iterator.id,
            properties: this.getProperties(),
            start: 0,
            end: this.totalRows()
        }).promise();
    }

    protected registerColumnDefinitionSubscriptions(): void {
        if (this.isDisposed)
            return;
        this.defineSubscription(this.columnDefinition, (columnDefinition) => {
            let columns = this.createColumns(columnDefinition);
            this.columns(columns);
        });
    }

    protected createColumns(columnDefinition: CoreFrameDTOs.IInlineEditingGridColumnDTO[]): Array<GridColumn> {
        if (this.isDisposed)
            return undefined;

        let columns = [];
        const showPDO = User.currentUserShouldShowPDOID();

        if (showPDO) {
            columns.push({
                name: 'PDO_ID',
                field: 'PDO_ID',
                title: 'PDO_ID',
                sortableTitle: 'pdo_id',
                isSortable: showPDO,
                sortValue: "PDO_ID",
                filterable: showPDO,
                hidden: !showPDO,
                value: (row) => { return row['pdoId']; },
                encoded: false,
                listViewPDOID: '0',
                typeName: 'System.String',
                uiGenerator: 'TextBox',
                possiblePDEnumerationValues: {},
                template: null
            });
        }

        columnDefinition.forEach((column: CoreFrameDTOs.IInlineEditingGridColumnDTO) => {
            if (!column.isVisible)
                return;

            const item = column.detailViewItem;
            InlineEditingGridManager.replaceUIGenerator(item);
            const isEditable = !InlineEditingGridManager.isTextColumnFactory(item.uiGenerator);

            let newCol: GridColumn = {
                title: this.getTitle(column),
                name: item.name,
                field: column.name,
                sortValue: column.name,
                typeName: column.dotNetTypeName,
                listViewPDOID: column.listViewPDOID,
                uiGenerator: column.uiGenerator,
                editor: (raw, row) => {
                    return this.getCellEditValue(row, item, isEditable);
                },
                isEditable: isEditable,
                value: (row: CoreFrameDTOs.IGenericDTO) => {
                    return this.getCellValue(row, item);
                },
                isSortable: !item.isToManyAssoziation,
                filterable: !item.isToManyAssoziation,
                hidden: false,
                possiblePDEnumerationValues: column.possiblePDEnumerationValues,
                showsTooltip: isEditable
            };

            const preset = this.columnPreset()[item.name];
            if (preset != null) {
                newCol.width = preset;
            }

            columns.push(newCol);
        });

        return columns;
    }

    protected getTitle(column: CoreFrameDTOs.IInlineEditingGridColumnDTO): string {
        const language = ResourceManager.getInstance().language.value();

        let title = column.mlsErgName[language];

        const unit = column.unitNames[language];
        if (title && unit)
            title += ` [${unit}]`;

        return title;
    }

    protected getCellSortValue(row: CoreFrameDTOs.IGenericDTO, item: CoreFrameDTOs.IDVGItemDTO): string {
        if (this.isDisposed)
            return undefined;

        let key = '';
        let value = '';
        if (item.isPDEmbeddedMedium) {
            key = item.name + '.Filename';
            value = row.values[key];
        }
        else if (item.isPDEmbedded || item.isPDEnumeration) {
            if (item.name.indexOf('.Value') === -1)
                key = item.name + '.Value';
            else
                key = item.name;

            if (item.isPDEmbedded) {
                value = row.values[key];
            } else {
                const technicalName = row.values[key];
                const ergName = item.possiblePDEnumerationValues[technicalName];
                value = ResourceManager.getInstance().get(ergName);
            }
        }
        else {
            if (item.listView != null && !item.isToManyAssoziation)
                key = item.name + '.ObjectIdentValue';
            else
                key = item.name;

            value = row.values[key];
        }

        if (Utilities.isFunction(value))
            return value();
        else
            return value;
    }

    protected getCellValue(row: CoreFrameDTOs.IGenericDTO, item: CoreFrameDTOs.IDVGItemDTO): HTMLElement {
        if (this.isDisposed)
            return undefined;

        let value = this.getCellSortValue(row, item);

        if (!Utilities.isNullOrUndefined(value))
            value = value.toString();
        else
            value = '';

        const printer = InlineEditingGridManager.getPrinter(item, value, row);
        const element = this.printCellValue(item, value, row, printer);

        let tooltip;
        if (Utilities.isNullOrUndefined(printer)) {
            tooltip = value;
        }
        else {
            tooltip = printer.createToolTip(item, value, row);
        }

        if (tooltip) { 
            // create tooltip binding
            ko.applyBindingsToNode(element, {
                tooltip: tooltip,
                locator: LocatorHelper.buildLocator(this.locator, 'disabled', item.name)
            });
        }

        return element;
    }

    private printCellValue(item: CoreFrameDTOs.IDVGItemDTO, value: string, row: CoreFrameDTOs.IGenericDTO, printer: IReadOnlyPrinterStatic): HTMLElement {
        if (Utilities.isNullOrUndefined(printer)) {
            const element = document.createElement('span');
            element.textContent = value;
            return element;
        }

        return printer.print(item, value, row);
    }

    protected getCellEditValue(row: CoreFrameDTOs.IGenericDTO, item: CoreFrameDTOs.IDVGItemDTO, isEditable: boolean): JQuery {
        if (this.isDisposed)
            return undefined;

        let editElement = document.createElement('div');

        // Editierbare Zellen (i.d.R. Controls) werden asynchron gerendert
        if (isEditable) {
            this.async(() => {
                this.renderEditValue(editElement, row, item, isEditable);
            });
        } else {
            // Nicht editierbare Zellen werden sofort gerendert, damit die Spaltenbreitenberechnung dort korrekt funktioniert
            this.renderEditValue(editElement, row, item, isEditable);
        }

        return $(editElement);
    }

    private renderEditValue(element: HTMLDivElement, row: CoreFrameDTOs.IGenericDTO, item: CoreFrameDTOs.IDVGItemDTO, isEditable: boolean): void {
        let factoryParams = this.getDefaultFactoryParametersForEditValue(row, item);
        const context = this.getEditContextForRow(row, true);
        factoryParams = _.extend(factoryParams, {
            context: context,
            detailViewContext: this.detailViewContext,
            locator: isEditable ? this.getCellLocator(element.parentElement) : ''
        });

        this.setInnerFactory(element, item);

        const factory = Factory.createInstance(item.uiGenerator, factoryParams, 'DummyControl') as ControlFactory;
        const tuple = factory.getTuple();
        const $control = factory.getControl();

        context.setCreatedFactory(factory);
        context.setCreatedTuple(item.name, tuple);

        element.appendChild($control[0]);
    }

    protected getCellLocator(cell: HTMLElement): ObservableOrDefault<string> {
        return LocatorHelper.buildLocator(this.locator, 'enabled');
    }

    private setInnerFactory(editElement: HTMLDivElement, item: CoreFrameDTOs.IDVGItemDTO) {
        if (!InlineEditingGridManager.isTextColumnFactory(item.uiGenerator))
            (editElement.parentNode as HTMLElement).classList.add('inner-factory');
    }

    protected getDefaultFactoryParametersForEditValue(row: CoreFrameDTOs.IGenericDTO, item: CoreFrameDTOs.IDVGItemDTO): interfaces.IControlFactoryConstructorParameter {
        return {
            detailViewItem: item
        };
    }

    private setOrderBy(sortExpression: ISortByCol[]): void {
        const orderBy = FilterHelper.createOrderExpression(sortExpression, this.columns(), this.detailViewItem.name, this.detailViewItem.listView.defaultOrder)
        this.orderBy(orderBy);
    }

    public onSortChange(sortExpression: ISortByCol[]): void {
        this.setOrderBy(sortExpression);
        this.refresh();
    }

    public onBeforeEdit(row: CoreFrameDTOs.IGenericDTO): JQueryPromise<boolean> {
        this.increaseBusyCounter()
        return RADEnvironmentProxy.getInstance().aquireLockObject({
            lockGroupID: this.lockGroupID,
            pdoID: row.pdoId
        }).then((success) => {
            if (success)
                this.addLock(row.pdoId);

            return success;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    public onSave(row: CoreFrameDTOs.IGenericDTO): JQueryPromise<boolean> {
        if (this.isBusy()) {
            return Utilities.resolveImmediately(false);
        }
        const editContext = this.getEditContextForRow(row);
        this.increaseBusyCounter();
        const updateProperties = editContext.updateAndValidateProperties();

        // Hilfs-Deferred
        const onSaveFinished = $.Deferred();

        // Beim Speichern muss zunächst noch einmal UpdateProperties aufgerufen werden
        // Erst danach kann das Objekt freigegeben und das Grid neugeladen werden
        $.when(updateProperties).then((result) => {
            if (!result) {
                onSaveFinished.resolve(false);
                return;
            }

            RADEnvironmentProxy.getInstance().releaseLockedInlineEditingObject({
                lockGroupID: this.lockGroupID,
                pdoID: row.pdoId
            })
                .then((result) => {
                    if (!result)
                        return false;
                    this.releaseLock(row.pdoId);
                    return true;
                })
                .then((couldReleaseLock) => {
                    if (couldReleaseLock) {
                        this.refresh();
                        return true;
                    }

                    return false;
                })
                .always((result: boolean) => {
                    if (result) {
                        // Hilfs-Deferred wird resolved
                        onSaveFinished.resolve(true);
                        let context = this.getEditContextForRow(row);

                        let factories = context.getCreatedFactories();
                        factories.forEach((factory) => {
                            if (!factory.isDisposed)
                                factory.dispose();
                        });

                        this.destroyEditContext(context);
                    } else
                        onSaveFinished.resolve(false);
                });
        });

        onSaveFinished.always(() => {
            this._savePromises.splice(this._savePromises.indexOf(onSaveFinished.promise()), 1);
            this.decreaseBusyCounter();
        });

        const promise = onSaveFinished.promise();
        this._savePromises.push(promise);

        return promise;
    }

    public onDelete(row: CoreFrameDTOs.IGenericDTO): JQueryPromise<boolean> {
        // Hier muss sich das initale Deferred gemerkt werden, damit dieses in der MessageBox resolved werden kann
        // Dadurch wird die Kette ausgelöst
        // Es muss jedoch die Kette zurückgegeben werden, damit diese korrekt im CoreFrameGrid erweitert werden kann
        let deferred = $.Deferred<boolean>();
        let chain = deferred.then((shouldDelete) => {
            if (!shouldDelete)
                return false;
            this.increaseBusyCounter();
            if (this.hasLock(row.pdoId))

                return RADEnvironmentProxy.getInstance().releaseLockedObject({
                    lockGroupID: this.lockGroupID,
                    pdoID: row.pdoId
                });
            return true;
        }).then<boolean|CoreFrameDTOs.IChangeObjectDTO>((result) => {
            if (result) {
                this.releaseLock(row.pdoId);
                return RADEnvironmentProxy.getInstance().disconnectAndDeleteInlineEditingGridRow({
                    pdTypeName: this.detailViewItem.listView.typeName,
                    pdoID: row.pdoId,
                    toPDOID: this.detailViewContext.genericObject.pdoId,
                    topdTypeName: this.inlineEditingContext().contextObjectPdTypeName,
                    assoziationName: this.inlineEditingContext().contextRelationName,
                });
            }
            return false;
        }).then((result: any) => {
            if (!result || !this.validateStatus(result))
                return false;
            else {
                this.dataSource.remove(row);
                this.detailViewContext.genericObject.isModified(true);
                this.raiseRootOnAssociationUpdated();
                return true;
            }
        }).always(() => {
            this.decreaseBusyCounter();
        });

        if (this.lockedObjects.length == 0 || this.hasLock(row.pdoId)) {
            let messageBox = MessageBoxViewModel.createDeletionMessageBox(null, () => { deferred.resolve(true); }, () => { deferred.resolve(false); });
            RADApplication.getInstance().showMessageBox(messageBox);
        } else {
            deferred.resolve(false);
        }

        return chain.promise();
    }

    public onAdd(): JQueryPromise<false | CoreFrameDTOs.IGenericDTO> {
        this.increaseBusyCounter();
        return RADEnvironmentProxy.getInstance().createAndLockInlineEditingObject({
            contextRelationName: this.detailViewItem.name,
            pdoID: this.detailViewContext.genericObject.pdoId,
            lockGroupID: this.lockGroupID,
            initialValues: {}
        }).then((result: CoreFrameDTOs.IGenericDTO) => {
            if (!this.validateStatus(result))
                return undefined;
            this.addLock(result.pdoId);
            this.dataSource.push(result);
            this.fireEditWithoutClick(result.pdoId);
            this.detailViewContext.genericObject.isModified(true);
            this.raiseRootOnAssociationUpdated();
            return result;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    public getEditContextForRow(row: CoreFrameDTOs.IGenericDTO, withUpdate: boolean = false): EditContext {
        let context = this.editContexts[row.pdoId];
        if (!context) {
            context = new InlineEditContext({
                inlineEditContext: this.inlineEditingContext(),
                parent: this.detailViewContext,
                uiListener: this.inlineEditingContext().detailView.uiListener,
                lockGroupID: this.lockGroupID,
                subject: row,
                defaultIterators: this.detailViewContext.defaultIterators,
                onPropertyValueUpdatedCallback: this.onRowPropertyValueUpdated.bind(this),
                onAssociationUpdatedCallback: this.onRowAssociationUpdated.bind(this)
            });

            this.editContexts[row.pdoId] = context;
            this.detailViewContext.registerNestedInlineEditingContext(context);
        }

        // Änderungen ins GenericObject übertragen
        let go = context.genericObject;
        let newGenericDTO = _.filter(this.dataSource(), (item) => { return item.pdoId == row.pdoId; });
        if (withUpdate && newGenericDTO.length == 1) {
            go.refresh(newGenericDTO[0]);
        }

        return context;
    }

    public getEditContexts(): EditContext[] {
        return _.values(this.editContexts);
    }

    private destroyEditContext(editContext: EditContext): void {
        const pdoId = editContext.genericObject.pdoId;
        this.detailViewContext?.removeNestedInlineEditingContext(editContext);
        editContext.dispose();
        delete this.editContexts[pdoId];
    }

    public addRowWithData(data: Dictionary<string | boolean | number>): JQueryPromise<boolean> {
        this.increaseBusyCounter();
        return RADEnvironmentProxy.getInstance().createAndLockInlineEditingObject({
            contextRelationName: this.detailViewItem.name,
            pdoID: this.detailViewContext.genericObject.pdoId,
            lockGroupID: this.lockGroupID,
            initialValues: JSON.stringify(data)
        }).then((result: CoreFrameDTOs.IGenericDTO) => {
            if (!this.validateStatus(result))
                return false;
            
            this.dataSource.push(result);
            this.detailViewContext.genericObject.isModified(true);

            return true;
        }).always(() => {
            this.decreaseBusyCounter();
        }).promise();
    }

    private addLock(pdoId: number | string) {
        if (typeof pdoId == 'string')
            pdoId = parseInt(pdoId);
        if (this.lockedObjects.indexOf(pdoId) == -1)
            this.lockedObjects.push(pdoId);
    }

    private releaseLock(pdoId: number | string) {
        if (typeof pdoId == 'string')
            pdoId = parseInt(pdoId);

        let index = this.lockedObjects.indexOf(pdoId);
        if (index > -1)
            this.lockedObjects.splice(index, 1);
    }

    private hasLock(pdoId: number | string) {
        if (typeof pdoId == 'string')
            pdoId = parseInt(pdoId);
        return this.lockedObjects.indexOf(pdoId) > -1;
    }

    /**
     * Creates parameter for creating inline editing context
     */
    protected getCreateContextParameter(): any {//Parameters<typeof RADEnvironmentProxy.createInlineEditingContext>[0] {
        return {
            pdTypeName: this.detailViewContext.genericObject.pdTypeName,
            contextPdoId: this.detailViewContext.genericObject.pdoId,
            contextRelationName: this.detailViewItem.name,
            detailViewContextID: this.detailViewContext.id,
            nestedContextName: this.detailViewContext.getNestedContextPath(),
            isSummaryViewMode: this.detailViewContext.isSummaryViewMode(),
            removeDefaultProperties: this.shouldRemoveDefaultProperties()
        };
    }

    protected destroyAllAsync(): void {
        const contexts = _.values(this.editContexts);
        this.editContexts = {};

        contexts.forEach(context => {
            this.async(() => this.destroyEditContext(context));
        });
    }

    private registerForMessages(): void {
        this.registerForMessageId('RefreshAssociationsMessage', this.refresh.bind(this));
    }

    private deleteIterator(iterator: CoreFrameDTOs.IIteratorDTO): JQueryPromise<boolean> {
        if (Utilities.isNullOrUndefined(iterator))
            return Utilities.resolveImmediately(true);

        return RADEnvironmentProxy.getInstance().deleteIterator({
            iteratorID: iterator.id
        }).then(() => {
            this.iterator = null;
            return true;
        }).promise();
    }

    public onColumnResized(columnName: string, newWidth: number): void {
        if (this.isDisposed)
            return;
        let preset = ko.toJS(this.columnPreset);
        preset[columnName] = newWidth + 'px';
        this.columnPreset(preset);
    }

    /**
     * @override
     */
    protected disposeInternal(): void {
        this.unregisterForMessageId('RefreshAssociationsMessage', null);
    
        if (this.detailViewContext && !this.detailViewContext.isDisposed) {
            // default iterator will be never used
            this.deleteIterator(this.detailViewContext.defaultIterators[this.detailViewItem.name]);
            this.deleteIterator(this.iterator);
            this.destroyAllAsync();
        }
        
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('InlineEditingGridViewModel', InlineEditingGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');